/**
 * React Query hooks for each dashboard widget's data endpoint.
 *
 * Co-located so future widgets follow the same naming + cache-key pattern.
 * staleTime defaults to 60s — widget data is mostly business metrics that
 * don't need second-by-second freshness, but refresh on revisit.
 *
 * Hooks that depend on the date range read it from DashboardRangeContext
 * and include the key in their React Query cache key. Changing the
 * dropdown automatically triggers a refetch with the new range.
 */

import { useQuery, type UseQueryResult } from "react-query";
import {
  fetchAgingReport,
  fetchAppointmentsSummary,
  fetchEstimatesSummary,
  fetchInvoicesSummary,
  fetchProjectsList,
  fetchRepairRequests,
  fetchRequestsByType,
  fetchSchedulesSummary,
  fetchServiceRequestsList,
  fetchTimeOffUpcoming,
} from "./api";
import { useDashboardRange } from "./RangeContext";
import type {
  AgingReport,
  AppointmentsSummary,
  EstimatesSummary,
  InvoicesSummary,
  InvoiceStatusFilter,
  ProjectsList,
  RepairRequestsSummary,
  RequestsByType,
  SchedulesSummary,
  ServiceRequestsList,
  TimeOffUpcoming,
} from "./types";

const COMMON = {
  staleTime: 60 * 1000,
  cacheTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
} as const;

export const useAppointmentsSummary = (): UseQueryResult<AppointmentsSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "appointments-summary", range],
    () => fetchAppointmentsSummary(range),
    COMMON,
  );
};

export const useInvoicesSummary = (): UseQueryResult<InvoicesSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "invoices-summary", range],
    () => fetchInvoicesSummary(range),
    COMMON,
  );
};

export const useEstimatesSummary = (): UseQueryResult<EstimatesSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "estimates-summary", range],
    () => fetchEstimatesSummary(range),
    COMMON,
  );
};

export const useRepairRequests = (): UseQueryResult<RepairRequestsSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "repair-requests", range],
    () => fetchRepairRequests(range),
    COMMON,
  );
};

export const useRequestsByType = (): UseQueryResult<RequestsByType> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "requests-by-type", range],
    () => fetchRequestsByType(range),
    COMMON,
  );
};

// Service Requests + Projects + Schedules don't react to the dashboard
// date dropdown — they show their own upcoming-by-date lists or use a
// fixed `days` window.
export const useServiceRequestsList = (): UseQueryResult<ServiceRequestsList> =>
  useQuery(["dashboard", "service-requests-list"], fetchServiceRequestsList, COMMON);

export const useProjectsList = (): UseQueryResult<ProjectsList> =>
  useQuery(["dashboard", "projects-list"], fetchProjectsList, COMMON);

export const useSchedulesSummary = (days = 14): UseQueryResult<SchedulesSummary> =>
  useQuery(["dashboard", "schedules-summary", days], () => fetchSchedulesSummary(days), COMMON);

export const useTimeOffUpcoming = (): UseQueryResult<TimeOffUpcoming> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "time-off-upcoming", range],
    () => fetchTimeOffUpcoming(range),
    COMMON,
  );
};

export const useAgingReport = (
  status: InvoiceStatusFilter = "open",
): UseQueryResult<AgingReport> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "aging-report", status, range],
    () => fetchAgingReport(status, range),
    COMMON,
  );
};

/** Helper for widget error display. */
export const formatQueryError = (err: unknown): string =>
  err instanceof Error ? err.message : "Failed to load";
